import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { Goal, GoalStatus, Task, Event } from '../types';
import { showToast } from '../utils/toast';

type DbGoal = Database['public']['Tables']['goals']['Row'];
type DbGoalInsert = Database['public']['Tables']['goals']['Insert'];
type DbGoalUpdate = Database['public']['Tables']['goals']['Update'];

const isValidUUID = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
const safeUUID = (id?: string | null) => (id && isValidUUID(id) ? id : null);

const mapStatusToUi = (dbStatus: string | null): GoalStatus => {
  if (dbStatus === 'achieved') return 'achieved';
  return 'active';
};

const mapStatusToDb = (uiStatus: GoalStatus): string => {
  if (uiStatus === 'achieved') return 'achieved';
  return 'in_progress';
};
const mapToGoal = (g: DbGoal): Goal => ({
  id: g.id,
  title: g.title,
  description: g.description || '',
  timeframe: '',
  status: mapStatusToUi(g.status),
  domainId: g.domain_id || '',
  tags: g.tags || [],
  budget: g.budget ?? null,
  parentGoalId: g.parent_goal_id ?? null,
  completedAt: g.completed_at ?? null,
});

export const goalService = {
  async getGoals(): Promise<Goal[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Vous devez être connecté.');

      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[goal.service] getGoals:', error.message);
        showToast('Erreur lors du chargement des objectifs', 'error');
        return [];
      }
      return (data || []).map(mapToGoal);
    } catch (err: any) {
      console.error('[goal.service] getGoals:', err.message);
      showToast('Erreur lors du chargement des objectifs', 'error');
      return [];
    }
  },

  async createGoal(goal: Partial<Goal>): Promise<Goal> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const insertData: DbGoalInsert = {
      user_id: user.id,
      title: goal.title || 'Nouvel objectif',
      description: goal.description || null,
      status: mapStatusToDb(goal.status || 'active') as DbGoalInsert['status'],
      domain_id: safeUUID(goal.domainId),
      tags: goal.tags || [],
      budget: goal.budget ?? null,
      parent_goal_id: safeUUID(goal.parentGoalId),
    };

    const { data, error } = await supabase.from('goals').insert(insertData).select().single();

    if (error) {
      console.error('[goal.service] createGoal:', error.message);
      showToast("Impossible de créer l'objectif", 'error');
      throw error;
    }
    return mapToGoal(data);
  },

  async updateGoal(id: string, updates: Partial<Goal>): Promise<Goal> {
    const updateData: DbGoalUpdate = {
      ...(updates.title !== undefined && { title: updates.title }),
      ...(updates.description !== undefined && { description: updates.description }),
      ...(updates.status !== undefined && {
        status: mapStatusToDb(updates.status) as DbGoalUpdate['status'],
      }),
      ...(updates.domainId !== undefined && { domain_id: safeUUID(updates.domainId) }),
      ...(updates.tags !== undefined && { tags: updates.tags }),
      ...(updates.budget !== undefined && { budget: updates.budget ?? null }),
      ...(updates.parentGoalId !== undefined && { parent_goal_id: safeUUID(updates.parentGoalId) }),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('goals')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[goal.service] updateGoal:', error.message);
      showToast("Impossible de mettre à jour l'objectif", 'error');
      throw error;
    }
    return mapToGoal(data);
  },

  async deleteGoal(id: string): Promise<void> {
    const { error } = await supabase.from('goals').delete().eq('id', id);

    if (error) {
      console.error('[goal.service] deleteGoal:', error.message);
      showToast("Impossible de supprimer l'objectif", 'error');
      throw error;
    }
  },
};

/**
 * Checks whether a goal should be auto-completed based on linked tasks/events.
 * Returns true if the goal was marked as achieved in DB.
 */
export async function checkAndCompleteGoal(
  goalId: string,
  userId: string,
  allTasks: Task[],
  allEvents: Event[],
  allGoals: Goal[]
): Promise<boolean> {
  const goal = allGoals.find((g) => g.id === goalId);
  if (!goal || goal.status === 'achieved') return false;

  // Collect events linked to this goal
  const linkedEvents = allEvents.filter((e) => e.goalId === goalId);

  // Collect tasks: directly linked + via linked events
  const linkedEventIds = new Set(linkedEvents.map((e) => e.id));
  const linkedTasks = allTasks.filter(
    (t) => t.goalId === goalId || (t.eventId && linkedEventIds.has(t.eventId))
  );

  // No tasks linked → cannot auto-complete
  if (linkedTasks.length === 0) return false;

  // All tasks must be done
  if (!linkedTasks.every((t) => t.status === 'done')) return false;

  // No future events allowed
  const today = new Date().toISOString().split('T')[0];
  if (linkedEvents.some((e) => e.date >= today)) return false;

  // All conditions met → mark achieved in DB
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('goals')
    .update({ status: 'achieved', completed_at: now, updated_at: now })
    .eq('id', goalId)
    .eq('user_id', userId);

  if (error) {
    console.error('[goal.service] checkAndCompleteGoal:', error.message);
    return false;
  }
  return true;
}

/**
 * Reverts a goal back to 'active' if at least one linked task is no longer done.
 * Mirror of checkAndCompleteGoal — called when a task is un-checked.
 * Returns true if the goal was reverted in DB.
 */
export async function revertGoalIfNeeded(
  goalId: string,
  userId: string,
  allTasks: Task[],
  allEvents: Event[],
  allGoals: Goal[]
): Promise<boolean> {
  const goal = allGoals.find((g) => g.id === goalId);
  if (!goal || goal.status !== 'achieved') return false;

  // Collect tasks using the same logic as checkAndCompleteGoal
  const linkedEvents = allEvents.filter((e) => e.goalId === goalId);
  const linkedEventIds = new Set(linkedEvents.map((e) => e.id));
  const linkedTasks = allTasks.filter(
    (t) => t.goalId === goalId || (t.eventId && linkedEventIds.has(t.eventId))
  );

  // If at least one task is not done, revert the goal
  if (linkedTasks.some((t) => t.status !== 'done')) {
    const { error } = await supabase
      .from('goals')
      .update({ status: 'in_progress', completed_at: null, updated_at: new Date().toISOString() })
      .eq('id', goalId)
      .eq('user_id', userId);

    if (error) {
      console.error('[goal.service] revertGoalIfNeeded:', error.message);
      return false;
    }
    return true;
  }
  return false;
}

/**
 * Force-completes a goal and all its descendant tasks / sub-goals recursively.
 * Used when the user manually switches a goal to "achieved".
 */
export async function completeGoalAndChildren(
  goalId: string,
  userId: string,
  allTasks: Task[],
  allGoals: Goal[]
): Promise<{ completedTaskIds: string[]; completedGoalIds: string[] }> {
  const now = new Date().toISOString();
  const completedTaskIds: string[] = [];
  const completedGoalIds: string[] = [];

  // Recursive helper to collect all descendant goal IDs
  const collectDescendantGoalIds = (parentId: string): string[] => {
    const children = allGoals.filter((g) => g.parentGoalId === parentId && g.status !== 'achieved');
    const ids: string[] = [];
    for (const child of children) {
      ids.push(child.id);
      ids.push(...collectDescendantGoalIds(child.id));
    }
    return ids;
  };

  const descendantGoalIds = collectDescendantGoalIds(goalId);
  const allGoalIds = [goalId, ...descendantGoalIds];

  // Complete all tasks directly linked to any of these goals
  const goalIdSet = new Set(allGoalIds);
  const tasksToComplete = allTasks.filter(
    (t) => t.goalId && goalIdSet.has(t.goalId) && t.status !== 'done'
  );

  for (const task of tasksToComplete) {
    const { error } = await supabase
      .from('tasks')
      .update({ status: 'done', completed_at: now, updated_at: now })
      .eq('id', task.id)
      .eq('user_id', userId);
    if (!error) completedTaskIds.push(task.id);
  }

  // Complete all goals (descendants first, then the root)
  for (const gId of [...descendantGoalIds, goalId]) {
    const { error } = await supabase
      .from('goals')
      .update({ status: 'achieved', completed_at: now, updated_at: now })
      .eq('id', gId)
      .eq('user_id', userId);
    if (!error) completedGoalIds.push(gId);
  }

  return { completedTaskIds, completedGoalIds };
}
